import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { postApiCall } from '@/service/httpCall';
import { API_AGENT_LEADS_TITLE } from '@/service/url';
import { friendlyApiError } from '@/utilities/apiErrorMessage';

// agent_lead_assignments.title is VARCHAR(255); the backend enforces the same rule.
export const LEAD_TITLE_MAX = 255;

export const leadTitleError = (title: string) => {
  const t = (title || '').trim();
  if (!t) return 'Title is required';
  if (t.length > LEAD_TITLE_MAX) return `Title must be at most ${LEAD_TITLE_MAX} characters`;
  return '';
};

const TRANSFER_MODE_LABEL: Record<string, string> = {
  all: 'all leads',
  pending: 'pending leads',
  completed: 'completed leads',
  specific: 'picked leads',
};

/** "12 pending leads" from transferred_from; '' for transfers made before modes existed. */
export const transferMovedLabel = (from: any) =>
  from?.mode
    ? `${Number(from.moved || 0).toLocaleString('en-IN')} ${TRANSFER_MODE_LABEL[from.mode] || 'leads'}`
    : '';

type EditTitleTarget = { id: number | string; title?: string | null } | null;

export function EditLeadTitleDialog({
  assignment,
  onClose,
  onSaved,
}: {
  assignment: EditTitleTarget;
  onClose: () => void;
  onSaved: (title: string) => void;
}) {
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!assignment) return;
    setValue(assignment.title || '');
    setTouched(false);
  }, [assignment]);

  const error = leadTitleError(value);
  const unchanged = value.trim() === (assignment?.title || '').trim();

  const save = () => {
    setTouched(true);
    if (!assignment || error || unchanged) return;
    const title = value.trim();
    setSaving(true);
    postApiCall(API_AGENT_LEADS_TITLE.replace(':id', String(assignment.id)), { title })
      .then(() => {
        toast.success('Title updated');
        onSaved(title);
        onClose();
      })
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Unable to update title'))
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={assignment !== null} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Edit title</DialogTitle>
            <DialogDescription>Agents and team leads see the new title on their leads list.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="lead-title-edit">
              Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="lead-title-edit"
              autoFocus
              value={value}
              maxLength={LEAD_TITLE_MAX}
              aria-invalid={touched && !!error}
              onChange={(e) => setValue(e.target.value)}
              onBlur={() => setTouched(true)}
            />
            {touched && error ? <p className="text-xs text-destructive">{error}</p> : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !!error || unchanged}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
